import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { useRequireLogin } from '../../utils/requireLogin';
import { apiFetchCached, invalidateApiCache } from '../../utils/apiClient';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { Autocomplete, TextField } from '@mui/material';
import { FiSliders, FiPlus, FiX, FiMapPin, FiTruck, FiPackage, FiList, FiHeart } from 'react-icons/fi';
import { useAnimatedPresence } from '../../hooks/useAnimatedPresence';
import InfiniteFeed from '../../components/InfiniteFeed/InfiniteFeed';
import MsvSelect from '../../components/MsvSelect/MsvSelect';
import WishlistHeartButton from '../../components/WishlistHeartButton/WishlistHeartButton';
import FooterSimple from '../../components/Footer/FooterSimple';
import BrowseSidebarSticky from '../../components/BrowseSidebarSticky/BrowseSidebarSticky';
import MarketplaceHubNav from '../../components/MarketplaceHubNav/MarketplaceHubNav';
import '../../components/MsvBrowseShell/MsvBrowseShell.css';
import {
    MARKETPLACE_DATE_SORT_OPTIONS,
    MARKETPLACE_PRICE_SORT_OPTIONS,
    getMarketplacePriceSortValue,
    isPriceSort,
    normalizeMarketplaceDateSort,
} from '../../utils/marketplaceSort';
import './Marketplace.css';
import '../../styles/msv-grid-shared.css';

import {
    driverIdPairs,
    seriesIdPairs,
    mainCategoriesIdPairs,
    diecastSubCategoriesIdPairs,
    raceUsedSubCategoriesIdPairs,
    driverWornSubCategoriesIdPairs,
    sheetMetalSubCategoriesIdPairs,
    teamIssuedSubCategoriesIdPairs,
    diecastYearCategoriesIdPairs,
    diecastVersionCategoriesIdPairs,
    tradingCardYearCategoriesIdPairs,
    tradingCardBrandCategoriesIdPairs,
    tradingCardTypeCategoriesIdPairs,
    heroCardYearCategoriesIdPairs,
    programsTicketsYearCategoriesIdPairs,
} from '../../constants/dropdownIdPairs';
import { US_STATE_FILTER_OPTIONS } from '../../constants/usStates';

const PAGE_SIZE = 20;
const STATE_KEY = 'marketplaceBrowseState';

const BROWSE_CATEGORIES = [
    { name: '', label: 'All items' },
    { name: 'Race Used / Team Issued', label: 'Race used' },
    { name: 'Diecasts', label: 'Diecasts' },
    { name: 'Trading Cards', label: 'Trading cards' },
    { name: 'Hero Cards / Post Cards', label: 'Hero cards' },
    { name: 'Programs / Tickets', label: 'Programs' },
    { name: 'Misc.', label: 'Misc.' },
];

const DRAWER_MAIN_CATEGORIES = BROWSE_CATEGORIES.map((cat, index) => ({
    id: cat.name || `mp-browse-${index}`,
    name: cat.name,
    label: cat.label,
}));

const emptyForm = {
    driverName: '',
    series: '',
    mainCategory: '',
    subCategory: '',
    subSubCategory: '',
    subSubSubCategory: '',
    minPrice: '',
    maxPrice: '',
    city: '',
    state: '',
    sortBy: '',
    shippingOnly: false,
};

const STATE_OPTIONS = US_STATE_FILTER_OPTIONS;

const getOptionLabelSafe = (option, labelKey) => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option[labelKey] != null ? String(option[labelKey]) : '';
};

const areOptionsEqualById = (option, value) => {
    if (!option || !value) return option === value;
    if (option.id !== undefined && value.id !== undefined) return option.id === value.id;
    return false;
};

const resolveSelected = (selected, options) => {
    if (!selected || !Array.isArray(options) || options.length === 0) return null;
    return options.find((o) => areOptionsEqualById(o, selected)) || null;
};

const formatPrice = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '$0';
    if (num % 1 === 0) return `$${num.toLocaleString('en-US')}`;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const timeAgo = (unix) => {
    if (!unix) return '';
    const seconds = Math.max(0, Math.floor(Date.now() / 1000) - Number(unix));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 86400 * 7) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(unix * 1000).toLocaleDateString();
};

const Marketplace = () => {
    const { user } = useContext(UserContext);
    const { authReady, isAuthenticated } = useRequireLogin('Please log in to browse the marketplace.');
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();

    const [formData, setFormData] = useState(emptyForm);
    const [showFilters, setShowFilters] = useState(false);
    const { mounted: filtersMounted, visible: filtersVisible } = useAnimatedPresence(showFilters);
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [stateRestored, setStateRestored] = useState(false);

    const [drivers, setDrivers] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [series, setSeries] = useState([]);
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [mainCategories, setMainCategories] = useState([]);
    const [selectedMainCategory, setSelectedMainCategory] = useState(null);
    const [diecastSubCategories, setDiecastSubCategories] = useState([]);
    const [selectedDiecastSubCategory, setSelectedDiecastSubCategory] = useState(null);
    const [raceUsedSubCategories, setraceUsedSubCategories] = useState([]);
    const [selectedRaceUsedSubCategory, setSelectedraceUsedSubCategory] = useState(null);
    const [driverWornSubCategories, setdriverWornSubCategories] = useState([]);
    const [selectedDriverWornSubCategory, setSelectedDriverWornSubCategory] = useState(null);
    const [sheetMetalSubCategories, setsheetMetalSubCategories] = useState([]);
    const [selectedSheetMetalSubCategory, setSelectedSheetMetalSubCategory] = useState(null);
    const [teamIssuedSubCategories, setTeamIssuedSubCategories] = useState([]);
    const [selectedTeamIssuedSubCategory, setSelectedTeamIssueSubCategory] = useState(null);
    const [diecastYearCategories, setDiecastYearCategories] = useState([]);
    const [selectedDiecastYearCategory, setSelectedDiecastYearCategory] = useState(null);
    const [diecastVersionCategories, setDiecastVersionCategories] = useState([]);
    const [selectedDiecastVersionCategory, setSelectedDiecastVersionCategory] = useState(null);
    const [tradingCardYearCategories, setTradingCardYearCategories] = useState([]);
    const [selectedTradingCardYearCategory, setSelectedTradingCardYearCategory] = useState(null);
    const [tradingCardBrandCategories, setTradingCardBrandCategories] = useState([]);
    const [selectedTradingCardBrandCategory, setSelectedTradingCardBrandCategory] = useState(null);
    const [tradingCardTypeCategories, setTradingCardTypeCategories] = useState([]);
    const [selectedTradingCardTypeCategory, setSelectedTradingCardTypeCategory] = useState(null);
    const [heroCardYearCategories, setHeroCardYearCategories] = useState([]);
    const [selectedHeroCardYearCategory, setSelectedHeroCardYearCategory] = useState(null);
    const [progamTicketYearCategories, setProgramTicketYearCategories] = useState([]);
    const [selectedProgramTicketYearCategory, setSelectedProgramTicketYearCategory] = useState(null);
    const [filtersReady, setFiltersReady] = useState(false);
    const [lastDateSort, setLastDateSort] = useState('');

    const formDataRef = useRef(formData);
    const nextTokenRef = useRef(null);
    const loadingRef = useRef(false);
    const requestIdRef = useRef(0);
    const pendingScrollRef = useRef(null);
    const stickyTopbarRef = useRef(null);
    const drawerBodyRef = useRef(null);

    formDataRef.current = formData;

    useEffect(() => {
        if (!isPriceSort(formData.sortBy)) {
            setLastDateSort(normalizeMarketplaceDateSort(formData.sortBy));
        }
    }, [formData.sortBy]);

    const scrollMarketplaceToTop = () => {
        pendingScrollRef.current = null;
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
    };

    const handleDateSortChange = (next) => {
        const nextDateSort = normalizeMarketplaceDateSort(next);
        setLastDateSort(nextDateSort);
        setFormData((prev) => ({
            ...prev,
            sortBy: isPriceSort(prev.sortBy) ? prev.sortBy : nextDateSort,
        }));
        scrollMarketplaceToTop();
    };

    const handlePriceSortChange = (next) => {
        setFormData((prev) => ({
            ...prev,
            sortBy: next || lastDateSort,
        }));
        scrollMarketplaceToTop();
    };

    const queryKey = useMemo(
        () =>
            JSON.stringify({
                driverName: formData.driverName,
                series: formData.series,
                mainCategory: formData.mainCategory,
                subCategory: formData.subCategory,
                subSubCategory: formData.subSubCategory,
                subSubSubCategory: formData.subSubSubCategory,
                minPrice: formData.minPrice,
                maxPrice: formData.maxPrice,
                city: formData.city,
                state: formData.state,
                sortBy: formData.sortBy,
                shippingOnly: formData.shippingOnly,
            }),
        [formData]
    );

    const resetNestedSelections = () => {
        setSelectedDiecastSubCategory(null);
        setSelectedraceUsedSubCategory(null);
        setSelectedDriverWornSubCategory(null);
        setSelectedSheetMetalSubCategory(null);
        setSelectedTeamIssueSubCategory(null);
        setSelectedDiecastYearCategory(null);
        setSelectedDiecastVersionCategory(null);
        setSelectedTradingCardYearCategory(null);
        setSelectedTradingCardBrandCategory(null);
        setSelectedTradingCardTypeCategory(null);
        setSelectedHeroCardYearCategory(null);
        setSelectedProgramTicketYearCategory(null);
    };

    useEffect(() => {
        // Only restore filters when returning from a listing (browser back or explicit Back).
        // A fresh visit from Home/Vault/etc. must start clean even if sessionStorage is stale.
        const shouldRestore =
            navigationType === 'POP' ||
            location.state?.restoreMarketplaceScroll === true;

        if (shouldRestore) {
            invalidateApiCache('/Marketplace/SearchMarketplace');
        }

        try {
            const saved = sessionStorage.getItem(STATE_KEY);
            if (shouldRestore && saved) {
                const parsed = JSON.parse(saved);
                if (parsed.formData && typeof parsed.formData === 'object') {
                    const { keyword: _ignoredKeyword, ...restoredForm } = parsed.formData;
                    setFormData({ ...emptyForm, ...restoredForm });
                }
                if (typeof parsed.scrollY === 'number') {
                    pendingScrollRef.current = parsed.scrollY;
                }
                const sel = parsed.selectedFilters;
                if (sel) {
                    setSelectedDriver(sel.selectedDriver || null);
                    setSelectedSeries(sel.selectedSeries || null);
                    setSelectedMainCategory(sel.selectedMainCategory || null);
                    setSelectedDiecastSubCategory(sel.selectedDiecastSubCategory || null);
                    setSelectedraceUsedSubCategory(sel.selectedRaceUsedSubCategory || null);
                    setSelectedDriverWornSubCategory(sel.selectedDriverWornSubCategory || null);
                    setSelectedSheetMetalSubCategory(sel.selectedSheetMetalSubCategory || null);
                    setSelectedTeamIssueSubCategory(sel.selectedTeamIssuedSubCategory || null);
                    setSelectedDiecastYearCategory(sel.selectedDiecastYearCategory || null);
                    setSelectedDiecastVersionCategory(sel.selectedDiecastVersionCategory || null);
                    setSelectedTradingCardYearCategory(sel.selectedTradingCardYearCategory || null);
                    setSelectedTradingCardBrandCategory(sel.selectedTradingCardBrandCategory || null);
                    setSelectedTradingCardTypeCategory(sel.selectedTradingCardTypeCategory || null);
                    setSelectedHeroCardYearCategory(sel.selectedHeroCardYearCategory || null);
                    setSelectedProgramTicketYearCategory(sel.selectedProgramTicketYearCategory || null);
                }
            } else if (saved) {
                sessionStorage.removeItem(STATE_KEY);
            }
        } catch {
            sessionStorage.removeItem(STATE_KEY);
        }

        if (!shouldRestore) {
            pendingScrollRef.current = null;
            window.scrollTo(0, 0);
        }

        setStateRestored(true);
    }, [navigationType, location.state?.restoreMarketplaceScroll]);

    useEffect(() => {
        setDrivers([...driverIdPairs].sort((a, b) => a.fullName.localeCompare(b.fullName)));
        setSeries([...seriesIdPairs].sort((a, b) => a.seriesName.localeCompare(b.seriesName)));
        setMainCategories(mainCategoriesIdPairs);
        setDiecastSubCategories(diecastSubCategoriesIdPairs);
        setraceUsedSubCategories(raceUsedSubCategoriesIdPairs);
        setdriverWornSubCategories(driverWornSubCategoriesIdPairs);
        setsheetMetalSubCategories(sheetMetalSubCategoriesIdPairs);
        setTeamIssuedSubCategories(teamIssuedSubCategoriesIdPairs);
        setDiecastYearCategories([...diecastYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setDiecastVersionCategories(
            [...diecastVersionCategoriesIdPairs].sort((a, b) => {
                if (a.name === 'Standard Base Version') return -1;
                if (b.name === 'Standard Base Version') return 1;
                return a.name.localeCompare(b.name);
            })
        );
        setTradingCardYearCategories([...tradingCardYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setTradingCardBrandCategories(tradingCardBrandCategoriesIdPairs);
        setTradingCardTypeCategories(tradingCardTypeCategoriesIdPairs);
        setHeroCardYearCategories([...heroCardYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setProgramTicketYearCategories([...programsTicketsYearCategoriesIdPairs].sort((a, b) => parseInt(b.name, 10) - parseInt(a.name, 10)));
        setFiltersReady(true);
    }, []);

    const fetchListings = useCallback(async ({ reset }) => {
        if (loadingRef.current && !reset) return;
        if (!reset && !nextTokenRef.current) return;

        const requestId = reset ? requestIdRef.current + 1 : requestIdRef.current;
        if (reset) requestIdRef.current = requestId;

        loadingRef.current = true;
        if (reset) {
            setLoadingSearch(true);
            setSearchResults([]);
            nextTokenRef.current = null;
            setHasMore(false);
            setApiErrorMessage('');
        } else {
            setLoadingMore(true);
        }

        const fd = formDataRef.current;
        try {
            const response = await apiFetchCached('/Marketplace/SearchMarketplace', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    DriverName: fd.driverName,
                    Series: fd.series,
                    MainCategory: fd.mainCategory,
                    SubCategory: fd.subCategory,
                    SubSubCategory: fd.subSubCategory,
                    SubSubSubCategory: fd.subSubSubCategory,
                    MinPrice: fd.minPrice ? parseFloat(fd.minPrice) : null,
                    MaxPrice: fd.maxPrice ? parseFloat(fd.maxPrice) : null,
                    City: fd.city || null,
                    State: fd.state || null,
                    SortBy: fd.sortBy || null,
                    ShippingAvailable: fd.shippingOnly || null,
                    PageSize: PAGE_SIZE,
                    PaginationToken: reset ? null : nextTokenRef.current,
                }),
            }, { ttlMs: 30000 });

            if (requestId !== requestIdRef.current) return;
            if (!response.ok) {
                if (reset) {
                    setSearchResults([]);
                    setApiErrorMessage('Search failed. Please try again.');
                }
                return;
            }

            const data = await response.json();
            const listings = Array.isArray(data?.marketplaceSearchResultDtos) ? data.marketplaceSearchResultDtos : [];
            const results = listings.map((item) => ({
                ...item,
                imageUrl: resolveImageSrc(item),
            }));

            nextTokenRef.current = data?.nextPageToken || null;
            setHasMore(Boolean(data?.nextPageToken));
            setSearchResults((prev) => (reset ? results : [...prev, ...results]));
            setApiErrorMessage('');
        } catch (err) {
            console.error(err);
            if (requestId !== requestIdRef.current) return;
            if (reset) {
                setSearchResults([]);
                setApiErrorMessage('An unexpected error occurred. Please try again.');
            }
        } finally {
            if (requestId === requestIdRef.current) {
                loadingRef.current = false;
                setLoadingSearch(false);
                setLoadingMore(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!authReady || !isAuthenticated || !filtersReady || !stateRestored) return;
        fetchListings({ reset: true });
    }, [queryKey, authReady, isAuthenticated, filtersReady, stateRestored, fetchListings]);

    useEffect(() => {
        if (pendingScrollRef.current == null || loadingSearch || searchResults.length === 0) return;
        const y = pendingScrollRef.current;
        pendingScrollRef.current = null;
        requestAnimationFrame(() => window.scrollTo(0, y));
    }, [loadingSearch, searchResults]);

    useEffect(() => {
        if (!filtersMounted) return undefined;
        document.body.classList.add('msv-filters-drawer-open');
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.classList.remove('msv-filters-drawer-open');
            document.body.style.overflow = previousOverflow;
        };
    }, [filtersMounted]);

    useEffect(() => {
        if (!showFilters) return;
        drawerBodyRef.current?.scrollTo(0, 0);
    }, [showFilters]);

    useEffect(() => {
        const el = stickyTopbarRef.current;
        if (!el) return undefined;

        const root = el.closest('.mp-main');
        if (!root) return undefined;

        const syncTopbarHeight = () => {
            root.style.setProperty('--mp-topbar-sticky-height', `${el.offsetHeight}px`);
        };

        syncTopbarHeight();
        const observer = new ResizeObserver(syncTopbarHeight);
        observer.observe(el);
        window.addEventListener('resize', syncTopbarHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', syncTopbarHeight);
        };
    }, []);

    const persistBrowseState = () => {
        sessionStorage.setItem(
            STATE_KEY,
            JSON.stringify({
                formData,
                scrollY: window.scrollY,
                selectedFilters: {
                    selectedDriver,
                    selectedSeries,
                    selectedMainCategory,
                    selectedDiecastSubCategory,
                    selectedRaceUsedSubCategory,
                    selectedDriverWornSubCategory,
                    selectedSheetMetalSubCategory,
                    selectedTeamIssuedSubCategory,
                    selectedDiecastYearCategory,
                    selectedDiecastVersionCategory,
                    selectedTradingCardYearCategory,
                    selectedTradingCardBrandCategory,
                    selectedTradingCardTypeCategory,
                    selectedHeroCardYearCategory,
                    selectedProgramTicketYearCategory,
                },
            })
        );
    };

    const handleNavigateToItem = (item) => {
        persistBrowseState();
        navigate(`/marketplaceItem/${item.marketplaceItemId}`);
    };

    const selectMainCategory = (name) => {
        const next = formData.mainCategory === name ? '' : name;
        const match = next ? mainCategories.find((c) => c.name === next) || null : null;
        setSelectedMainCategory(match);
        resetNestedSelections();
        setFormData((prev) => ({
            ...prev,
            mainCategory: next,
            subCategory: '',
            subSubCategory: '',
            subSubSubCategory: '',
        }));
    };

    const selectSubCategory = (name) => {
        const next = formData.subCategory === name ? '' : name;
        resetNestedSelections();
        if (formData.mainCategory === 'Diecasts') {
            setSelectedDiecastSubCategory(diecastSubCategories.find((x) => x.name === next) || null);
        }
        if (formData.mainCategory === 'Race Used / Team Issued') {
            setSelectedraceUsedSubCategory(raceUsedSubCategories.find((x) => x.name === next) || null);
        }
        setFormData((prev) => ({
            ...prev,
            subCategory: next,
            subSubCategory: '',
            subSubSubCategory: '',
        }));
    };

    const clearFilters = () => {
        setFormData(emptyForm);
        setLastDateSort('');
        setSelectedDriver(null);
        setSelectedSeries(null);
        setSelectedMainCategory(null);
        resetNestedSelections();
        sessionStorage.removeItem(STATE_KEY);
        setShowFilters(false);
    };

    const removeChip = (key) => {
        if (key === 'mainCategory') {
            selectMainCategory(formData.mainCategory);
            return;
        }
        if (key === 'subCategory') {
            selectSubCategory(formData.subCategory);
            return;
        }
        if (key === 'driverName') setSelectedDriver(null);
        if (key === 'series') setSelectedSeries(null);
        if (key === 'subSubCategory') {
            setSelectedDiecastYearCategory(null);
            setSelectedSheetMetalSubCategory(null);
            setSelectedDriverWornSubCategory(null);
            setSelectedTeamIssueSubCategory(null);
            setSelectedTradingCardBrandCategory(null);
            setSelectedHeroCardYearCategory(null);
            setSelectedProgramTicketYearCategory(null);
        }
        if (key === 'subSubSubCategory') {
            setSelectedDiecastVersionCategory(null);
            setSelectedTradingCardTypeCategory(null);
        }
        setFormData((prev) => ({
            ...prev,
            [key]: key === 'shippingOnly' ? false : '',
            ...(key === 'subCategory' ? { subSubCategory: '', subSubSubCategory: '' } : {}),
            ...(key === 'subSubCategory' ? { subSubSubCategory: '' } : {}),
        }));
    };

    if (!authReady || !isAuthenticated || !filtersReady || !stateRestored) return null;

    const safeDriver = resolveSelected(selectedDriver, drivers);
    const safeSeries = resolveSelected(selectedSeries, series);
    const safeMainCat = resolveSelected(selectedMainCategory, mainCategories);
    const safeDiecastSub = resolveSelected(selectedDiecastSubCategory, diecastSubCategories);
    const safeDiecastYr = resolveSelected(selectedDiecastYearCategory, diecastYearCategories);
    const safeDiecastVer = resolveSelected(selectedDiecastVersionCategory, diecastVersionCategories);
    const safeRaceUsed = resolveSelected(selectedRaceUsedSubCategory, raceUsedSubCategories);
    const safeSheetMetal = resolveSelected(selectedSheetMetalSubCategory, sheetMetalSubCategories);
    const safeTeamIssued = resolveSelected(selectedTeamIssuedSubCategory, teamIssuedSubCategories);
    const safeDriverWorn = resolveSelected(selectedDriverWornSubCategory, driverWornSubCategories);
    const safeTCYear = resolveSelected(selectedTradingCardYearCategory, tradingCardYearCategories);
    const safeTCBrand = resolveSelected(selectedTradingCardBrandCategory, tradingCardBrandCategories);
    const safeTCType = resolveSelected(selectedTradingCardTypeCategory, tradingCardTypeCategories);
    const safeHeroYr = resolveSelected(selectedHeroCardYearCategory, heroCardYearCategories);
    const safeProgYr = resolveSelected(selectedProgramTicketYearCategory, progamTicketYearCategories);

    const activeSubcategories =
        formData.mainCategory === 'Diecasts'
            ? diecastSubCategories
            : formData.mainCategory === 'Race Used / Team Issued'
                ? raceUsedSubCategories
                : [];

    const drawerMainCat =
        DRAWER_MAIN_CATEGORIES.find((cat) => cat.name === formData.mainCategory)
        ?? DRAWER_MAIN_CATEGORIES[0];

    const drawerFilterKeys = new Set([
        'mainCategory', 'subCategory',
        'minPrice', 'maxPrice', 'city', 'state', 'shippingOnly',
        'subSubCategory', 'subSubSubCategory',
    ]);

    const filterChips = [
        formData.mainCategory && { key: 'mainCategory', label: formData.mainCategory },
        formData.subCategory && { key: 'subCategory', label: formData.subCategory },
        formData.subSubCategory && { key: 'subSubCategory', label: formData.subSubCategory },
        formData.subSubSubCategory && { key: 'subSubSubCategory', label: formData.subSubSubCategory },
        formData.driverName && { key: 'driverName', label: formData.driverName },
        formData.series && { key: 'series', label: formData.series },
        formData.minPrice && { key: 'minPrice', label: `Min ${formatPrice(formData.minPrice)}` },
        formData.maxPrice && { key: 'maxPrice', label: `Max ${formatPrice(formData.maxPrice)}` },
        formData.city && { key: 'city', label: formData.city },
        formData.state && { key: 'state', label: formData.state },
        formData.shippingOnly && { key: 'shippingOnly', label: 'Ships' },
    ].filter(Boolean);

    const drawerFilterCount = filterChips.filter((chip) => drawerFilterKeys.has(chip.key)).length;
    const heading = formData.mainCategory
        ? formData.subCategory
            ? formData.subCategory
            : BROWSE_CATEGORIES.find((c) => c.name === formData.mainCategory)?.label || formData.mainCategory
        : 'All items';

    const ac = (options, label, value, onChange, labelKey, disabled) => (
        <Autocomplete
            disablePortal
            options={options}
            getOptionLabel={(o) => getOptionLabelSafe(o, labelKey)}
            isOptionEqualToValue={areOptionsEqualById}
            value={value}
            onChange={onChange}
            disabled={!!disabled}
            renderInput={(params) => <TextField {...params} label={label} size="small" />}
            sx={{ width: '100%' }}
        />
    );

    return (
        <div className="mp-page msv-browse-page">
            <aside className="mp-sidebar">
                <BrowseSidebarSticky>
                    <p className="msv-page-eyebrow">Buy &amp; sell</p>
                    <h1>Marketplace</h1>
                    <button type="button" className="mp-sell-btn" onClick={() => navigate('/createmarketplaceitem')}>
                        <FiPlus />
                        Create listing
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/createmarketplaceitem', { state: { pickFromCollection: true } })}
                    >
                        Sell from my collection
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/mymarketplacelistings')}
                    >
                        <FiList aria-hidden />
                        My listings
                    </button>
                    <button
                        type="button"
                        className="mp-sell-from-collection-btn"
                        onClick={() => navigate('/myHeroCardWishList?type=marketplace')}
                    >
                        <FiHeart aria-hidden />
                        Wishlist
                    </button>

                    <p className="mp-sidebar-label">Browse</p>
                    <nav className="mp-cat-list">
                        {BROWSE_CATEGORIES.map((cat) => (
                            <button
                                key={cat.label}
                                type="button"
                                className={`mp-cat-item ${formData.mainCategory === cat.name ? 'active' : ''}`}
                                onClick={() => selectMainCategory(cat.name)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </nav>

                    {activeSubcategories.length > 0 && (
                        <>
                            <p className="mp-sidebar-label">Filters</p>
                            <nav className="mp-cat-list mp-cat-list-nested">
                                {activeSubcategories.map((sub) => (
                                    <button
                                        key={sub.id}
                                        type="button"
                                        className={`mp-cat-item ${formData.subCategory === sub.name ? 'active' : ''}`}
                                        onClick={() => selectSubCategory(sub.name)}
                                    >
                                        {sub.name}
                                    </button>
                                ))}
                            </nav>
                        </>
                    )}
                </BrowseSidebarSticky>
            </aside>

            <div className="mp-main">
                <div className="mp-sticky-topbar" ref={stickyTopbarRef}>
                <div className="mp-topbar msv-browse-toolbar">
                    <div className="msv-browse-filter-form mp-topbar-filters">
                        <div className="msv-browse-filter-row">
                        <Autocomplete
                            disablePortal
                            options={drivers}
                            getOptionLabel={(o) => getOptionLabelSafe(o, 'fullName')}
                            isOptionEqualToValue={areOptionsEqualById}
                            value={safeDriver}
                            onChange={(_, v) => {
                                setSelectedDriver(v);
                                setFormData((p) => ({ ...p, driverName: v?.fullName || '' }));
                                if (!v) setSelectedSeries(null);
                            }}
                            disabled={!!safeSeries}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Driver"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        style: safeSeries
                                            ? { backgroundColor: '#f0f0f0', opacity: 0.6, cursor: 'not-allowed' }
                                            : {},
                                    }}
                                />
                            )}
                            sx={{ width: '100%', marginRight: 0 }}
                        />
                        <Autocomplete
                            disablePortal
                            options={series}
                            getOptionLabel={(o) => getOptionLabelSafe(o, 'seriesName')}
                            isOptionEqualToValue={areOptionsEqualById}
                            value={safeSeries}
                            onChange={(_, v) => {
                                setSelectedSeries(v);
                                setFormData((p) => ({ ...p, series: v?.seriesName || '' }));
                                if (!v) setSelectedDriver(null);
                            }}
                            disabled={!!safeDriver}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Series"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        style: safeDriver
                                            ? { backgroundColor: '#f0f0f0', opacity: 0.6, cursor: 'not-allowed' }
                                            : {},
                                    }}
                                />
                            )}
                            sx={{ width: '100%', marginRight: 0 }}
                        />
                        <MsvSelect
                            id="mp-date-sort"
                            label="Sort"
                            className="mp-sort-field mp-date-sort-field msv-sort-field is-row is-compact"
                            value={lastDateSort}
                            options={MARKETPLACE_DATE_SORT_OPTIONS}
                            onChange={handleDateSortChange}
                        />
                        <MsvSelect
                            id="mp-price-sort"
                            label="Price"
                            className="mp-sort-field mp-price-sort-field msv-sort-field is-row is-compact"
                            value={getMarketplacePriceSortValue(formData.sortBy)}
                            options={MARKETPLACE_PRICE_SORT_OPTIONS}
                            onChange={handlePriceSortChange}
                        />
                        <button
                            type="button"
                            className={`msv-browse-filters-btn mp-filters-btn${showFilters || drawerFilterCount ? ' is-active' : ''}`}
                            onClick={() => setShowFilters(true)}
                        >
                            <FiSliders aria-hidden />
                            Filters{drawerFilterCount ? ` · ${drawerFilterCount}` : ''}
                        </button>
                        </div>
                    </div>
                </div>

                {filterChips.length > 0 && (
                    <div className="mp-active-filters">
                        {filterChips.map((chip) => (
                            <button key={chip.key} type="button" className="mp-active-chip" onClick={() => removeChip(chip.key)}>
                                {chip.label}
                                <FiX />
                            </button>
                        ))}
                        <button type="button" className="mp-clear-link" onClick={clearFilters}>
                            Clear all
                        </button>
                    </div>
                )}
                </div>

                <div className="mp-heading-row">
                    <div className="mp-heading-block">
                        <p className="msv-page-eyebrow">Marketplace</p>
                        <h2>{heading}</h2>
                    </div>
                    <span className="mp-listing-count">
                        {loadingSearch
                            ? 'Loading…'
                            : `${searchResults.length}${hasMore ? '+' : ''} result${searchResults.length === 1 ? '' : 's'}`}
                    </span>
                </div>

                {loadingSearch && searchResults.length === 0 ? (
                    <div className="mp-grid">
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div key={`sk-${i}`} className="mp-listing-card mp-skeleton">
                                <div className="mp-card-image" />
                                <div className="mp-card-body">
                                    <div className="mp-skel-line mp-skel-price" />
                                    <div className="mp-skel-line" />
                                    <div className="mp-skel-line mp-skel-short" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : searchResults.length === 0 && !apiErrorMessage ? (
                    <div className="mp-empty">
                        <div className="mp-empty-icon" aria-hidden>
                            <FiPackage />
                        </div>
                        <h3>No listings yet</h3>
                        <p>Try a different category or clear your filters.</p>
                        {filterChips.length > 0 && (
                            <button type="button" className="mp-sell-btn" onClick={clearFilters}>
                                Clear filters
                            </button>
                        )}
                    </div>
                ) : searchResults.length > 0 ? (
                    <>
                        <InfiniteFeed
                            items={searchResults}
                            getKey={(item) => item.marketplaceItemId}
                            hasMore={hasMore}
                            loading={loadingSearch && searchResults.length === 0}
                            loadingMore={loadingMore}
                            emptyMessage="No listings yet"
                            onLoadMore={() => {
                                if (!hasMore || loadingRef.current) return;
                                fetchListings({ reset: false });
                            }}
                            renderItem={(item) => {
                                const location = [item.city, item.state].filter(Boolean).join(', ');
                                const isOwnListing =
                                    user?.userId != null &&
                                    Number(item.userId) === Number(user.userId);
                                const openItem = () => handleNavigateToItem(item);
                                return (
                                    <div
                                        className="mp-listing-card"
                                        role="link"
                                        tabIndex={0}
                                        onClick={openItem}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                openItem();
                                            }
                                        }}
                                    >
                                        <div className="mp-card-image">
                                            {item.imageUrl ? (
                                                <img src={item.imageUrl} alt="" loading="lazy" decoding="async" />
                                            ) : (
                                                <span>No photo</span>
                                            )}
                                            <WishlistHeartButton itemType="marketplace" item={item} />
                                            {isOwnListing && (
                                                <span className="mp-you-own-badge">Your listing</span>
                                            )}
                                        </div>
                                        <div className="mp-card-body">
                                            <p className="mp-card-price">{formatPrice(item.price)}</p>
                                            <p className="mp-card-title">{toTitleCase(item.title)}</p>
                                            <p className="mp-card-meta">
                                                {location && (
                                                    <span>
                                                        <FiMapPin /> {location}
                                                    </span>
                                                )}
                                                {item.dateTimeAddedUnix ? <span>{timeAgo(item.dateTimeAddedUnix)}</span> : null}
                                            </p>
                                            {(item.shippingAvailable || item.localPickupAvailable !== false) && (
                                                <div className="mp-card-fulfillment">
                                                    {item.shippingAvailable && (
                                                        <p className="mp-card-ship">
                                                            <FiTruck /> Ships
                                                        </p>
                                                    )}
                                                    {item.localPickupAvailable !== false && (
                                                        <p className="mp-card-ship">
                                                            <FiMapPin /> Pickup
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        {!loadingSearch && !loadingMore && !hasMore && (
                            <p className="mp-end">You’re all caught up.</p>
                        )}
                    </>
                ) : null}

                {apiErrorMessage && (
                    <div className="mp-empty">
                        <p>{apiErrorMessage}</p>
                    </div>
                )}
            </div>

            {/* Outside sticky head so position:fixed pins to the viewport (Vault-style phone tab bar) */}
            <div className="mp-mobile-tabbar" aria-label="Section navigation">
                <MarketplaceHubNav layout="chips" />
            </div>

            {filtersMounted && (
                <div className={`mp-drawer-root${filtersVisible ? ' is-open' : ''}`}>
                    <button type="button" className="mp-drawer-backdrop" aria-label="Close filters" onClick={() => setShowFilters(false)} />
                    <aside className="mp-drawer" role="dialog" aria-label="Filters">
                        <div className="mp-drawer-header">
                            <h2>Filters</h2>
                            <button type="button" className="mp-icon-btn" onClick={() => setShowFilters(false)} aria-label="Close">
                                <FiX />
                            </button>
                        </div>

                        <div className="mp-drawer-body" ref={drawerBodyRef}>
                            <div className="mp-drawer-category-filters">
                                {ac(
                                    DRAWER_MAIN_CATEGORIES,
                                    'Category',
                                    drawerMainCat,
                                    (_, v) => {
                                        const nextName = v?.name ?? '';
                                        setSelectedMainCategory(
                                            nextName ? mainCategories.find((c) => c.name === nextName) || null : null,
                                        );
                                        resetNestedSelections();
                                        setFormData((p) => ({
                                            ...p,
                                            mainCategory: nextName,
                                            subCategory: '',
                                            subSubCategory: '',
                                            subSubSubCategory: '',
                                        }));
                                    },
                                    'label'
                                )}

                                {formData.mainCategory === 'Diecasts' &&
                                    ac(diecastSubCategories, 'Scale / type', safeDiecastSub, (_, v) => {
                                        setSelectedDiecastSubCategory(v);
                                        setSelectedDiecastYearCategory(null);
                                        setSelectedDiecastVersionCategory(null);
                                        setFormData((p) => ({
                                            ...p,
                                            subCategory: v?.name || '',
                                            subSubCategory: '',
                                            subSubSubCategory: '',
                                        }));
                                    }, 'name')}

                                {formData.mainCategory === 'Race Used / Team Issued' &&
                                    ac(raceUsedSubCategories, 'Type', safeRaceUsed, (_, v) => {
                                        setSelectedraceUsedSubCategory(v);
                                        setSelectedSheetMetalSubCategory(null);
                                        setSelectedDriverWornSubCategory(null);
                                        setSelectedTeamIssueSubCategory(null);
                                        setFormData((p) => ({ ...p, subCategory: v?.name || '', subSubCategory: '' }));
                                    }, 'name')}
                            </div>

                            <div className="mp-price-row">
                                <label className="mp-field">
                                    <span>Min price</span>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="$"
                                        value={formData.minPrice}
                                        onChange={(e) => setFormData((p) => ({ ...p, minPrice: e.target.value }))}
                                    />
                                </label>
                                <label className="mp-field">
                                    <span>Max price</span>
                                    <input
                                        type="number"
                                        min="0"
                                        placeholder="$"
                                        value={formData.maxPrice}
                                        onChange={(e) => setFormData((p) => ({ ...p, maxPrice: e.target.value }))}
                                    />
                                </label>
                            </div>

                            <label className="mp-check">
                                <input
                                    type="checkbox"
                                    checked={formData.shippingOnly}
                                    onChange={(e) => setFormData((p) => ({ ...p, shippingOnly: e.target.checked }))}
                                />
                                Shipping available
                            </label>

                            <div className="mp-price-row">
                                <label className="mp-field">
                                    <span>Item City</span>
                                    <input
                                        type="text"
                                        placeholder="Item city"
                                        value={formData.city}
                                        onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                                    />
                                </label>
                                <MsvSelect
                                    id="mp-drawer-state"
                                    label="Item State"
                                    fullWidth
                                    filterable
                                    placeholder="Type or select state…"
                                    value={formData.state}
                                    options={STATE_OPTIONS}
                                    onChange={(next) => setFormData((p) => ({ ...p, state: next }))}
                                />
                            </div>

                            {safeMainCat?.name === 'Diecasts' &&
                                safeDiecastSub &&
                                ac(diecastYearCategories, 'Year', safeDiecastYr, (_, v) => {
                                    setSelectedDiecastYearCategory(v);
                                    setSelectedDiecastVersionCategory(null);
                                    setFormData((p) => ({ ...p, subSubCategory: v?.name || '', subSubSubCategory: '' }));
                                }, 'name')}
                            {safeMainCat?.name === 'Diecasts' &&
                                safeDiecastSub &&
                                safeDiecastYr &&
                                ac(diecastVersionCategories, 'Version', safeDiecastVer, (_, v) => {
                                    setSelectedDiecastVersionCategory(v);
                                    setFormData((p) => ({ ...p, subSubSubCategory: v?.name || '' }));
                                }, 'name')}

                            {safeRaceUsed?.name === 'Sheet Metal / Body Panel' &&
                                ac(sheetMetalSubCategories, 'Panel', safeSheetMetal, (_, v) => {
                                    setSelectedSheetMetalSubCategory(v);
                                    setFormData((p) => ({ ...p, subSubCategory: v?.name || '' }));
                                }, 'name')}
                            {safeRaceUsed?.name === 'Team Issued' &&
                                ac(teamIssuedSubCategories, 'Item', safeTeamIssued, (_, v) => {
                                    setSelectedTeamIssueSubCategory(v);
                                    setFormData((p) => ({ ...p, subSubCategory: v?.name || '' }));
                                }, 'name')}
                            {safeRaceUsed?.name === 'Driver Worn' &&
                                ac(driverWornSubCategories, 'Item', safeDriverWorn, (_, v) => {
                                    setSelectedDriverWornSubCategory(v);
                                    setFormData((p) => ({ ...p, subSubCategory: v?.name || '' }));
                                }, 'name')}

                            {safeMainCat?.name === 'Trading Cards' &&
                                ac(tradingCardYearCategories, 'Year', safeTCYear, (_, v) => {
                                    setSelectedTradingCardYearCategory(v);
                                    setSelectedTradingCardBrandCategory(null);
                                    setSelectedTradingCardTypeCategory(null);
                                    setFormData((p) => ({ ...p, subCategory: v?.name || '', subSubCategory: '', subSubSubCategory: '' }));
                                }, 'name')}
                            {safeMainCat?.name === 'Trading Cards' &&
                                safeTCYear &&
                                ac(tradingCardBrandCategories, 'Brand', safeTCBrand, (_, v) => {
                                    setSelectedTradingCardBrandCategory(v);
                                    setSelectedTradingCardTypeCategory(null);
                                    setFormData((p) => ({ ...p, subSubCategory: v?.name || '', subSubSubCategory: '' }));
                                }, 'name')}
                            {safeMainCat?.name === 'Trading Cards' &&
                                safeTCBrand &&
                                ac(tradingCardTypeCategories, 'Type', safeTCType, (_, v) => {
                                    setSelectedTradingCardTypeCategory(v);
                                    setFormData((p) => ({ ...p, subSubSubCategory: v?.name || '' }));
                                }, 'name')}

                            {safeMainCat?.name === 'Hero Cards / Post Cards' &&
                                ac(heroCardYearCategories, 'Year', safeHeroYr, (_, v) => {
                                    setSelectedHeroCardYearCategory(v);
                                    setFormData((p) => ({ ...p, subCategory: v?.name || '' }));
                                }, 'name')}
                            {safeMainCat?.name === 'Programs / Tickets' &&
                                ac(progamTicketYearCategories, 'Year', safeProgYr, (_, v) => {
                                    setSelectedProgramTicketYearCategory(v);
                                    setFormData((p) => ({ ...p, subCategory: v?.name || '' }));
                                }, 'name')}
                        </div>

                        <div className="mp-drawer-footer">
                            <button type="button" className="mp-clear-btn" onClick={clearFilters}>
                                Reset
                            </button>
                            <button type="button" className="mp-sell-btn" onClick={() => setShowFilters(false)}>
                                See listings
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            <div className="msv-browse-site-footer">
                <FooterSimple />
            </div>
        </div>
    );
};

export default Marketplace;
